import { Button, Card, Space } from 'antd'
import { compact, isEmpty, join } from 'lodash'
import React, { useEffect, useMemo } from 'react'
import './PersonalInfoCard.css'

function PersonalInfoCard({
    info,
    aboutInfo,
    basicInfo,
    contact,
    preQualify,
    isMobile,
    showSideMenu,
}) {
    const languages = useMemo(() => {
        if (isEmpty(basicInfo.languages)) {
            return ''
        }
        return basicInfo.languages.join(', ')
    }, [basicInfo.languages])

    const name = useMemo(() => {
        return join(
            compact([
                basicInfo.firstname,
                basicInfo.middlename,
                basicInfo.lastname,
            ]),
            ' '
        )
    }, [basicInfo])

    const validContactInfos = useMemo(() => {
        const result = []
        if (!isEmpty(aboutInfo.phone)) {
            result.push({
                icon: 'phone-blue.svg',
                text: aboutInfo.phone,
                alt: 'Phone',
            })
        }
        if (!isEmpty(aboutInfo.email)) {
            result.push({
                icon: 'email-blue.svg',
                text: aboutInfo.email,
                alt: 'Email',
            })
        }
        if (!isEmpty(aboutInfo.companyAddress)) {
            result.push({
                icon: 'location-blue.svg',
                text: aboutInfo.companyAddress,
                alt: 'Location',
            })
        }
        return result
    }, [aboutInfo.phone, aboutInfo.email, aboutInfo.companyAddress])

    const socials = useMemo(() => {
        // Define supported social platforms
        const platforms = ['google', 'linkedin', 'twitter', 'yelp']

        // Map through platforms and create array of active social links
        return platforms.reduce((acc, platform) => {
            if (basicInfo[platform]) {
                acc.push({
                    platform,
                    link: basicInfo[platform],
                })
            }
            return acc
        }, [])
    }, [basicInfo])

    const dynamicExternalLink = (href) => {
        if (!/^https?:\/\//i.test(href)) {
            return `https://${href}`
        }
        return href
    }

    useEffect(() => {
        const fixedBottom = document.querySelector(
            '.personal-info-fixed-bottom'
        )
        // For mobile loan officer detail page, we need to add a padding bottom to the footer to avoid the content being cut off
        if (isMobile && fixedBottom) {
            // eslint-disable-next-line no-unused-expressions
            document.querySelector('footer')?.classList.add('pb-70-safe-area')

            return () => {
                // eslint-disable-next-line no-unused-expressions
                document
                    .querySelector('footer')
                    ?.classList.remove('pb-70-safe-area')
            }
        }
        return () => {}
    }, [isMobile])

    if (isMobile) {
        return (
            <>
                <div style={{ width: '100%', marginBottom: '14px' }}>
                    <Card
                        className="personal-info-card"
                        styles={{ body: { padding: '20px' } }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                gap: '15px',
                                marginBottom: '20px',
                            }}
                        >
                            <div style={{ flexShrink: 0 }}>
                                {!isEmpty(basicInfo.file) &&
                                basicInfo.file?.[0]?.url ? (
                                    <img
                                        src={basicInfo.file[0].url}
                                        alt={basicInfo.name}
                                        className="personal-info-avatar"
                                    />
                                ) : (
                                    <div
                                        className="personal-info-avatar"
                                        style={{
                                            backgroundColor: '#d9d9d9',
                                        }}
                                        aria-label={basicInfo.name}
                                    />
                                )}
                            </div>

                            {/* Name and NMLS */}
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}
                            >
                                <div className="personal-info-name">{name}</div>

                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                    }}
                                >
                                    {/* NMLS Number and Verification Status */}
                                    <div className="personal-info-label">
                                        NMLS
                                        <span className="personal-info-value">{` # ${basicInfo.nmls}`}</span>
                                    </div>

                                    {/* Company Information */}
                                    <div className="personal-info-label">
                                        Works at
                                        <span className="personal-info-value">{` ${aboutInfo.companyName}`}</span>
                                    </div>

                                    {/* Language */}
                                    <div className="personal-info-label">
                                        Language
                                        <span className="personal-info-value">{` ${languages}`}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="personal-info-contact">
                            <Space
                                direction="vertical"
                                size={12}
                                className="personal-info-contact-space"
                            >
                                {validContactInfos.map((item, index) => (
                                    <div
                                        key={index}
                                        className="personal-info-contact-item"
                                    >
                                        <img
                                            src={`/images/${item.icon}`}
                                            alt={item.alt}
                                        />
                                        <span>{item.text}</span>
                                    </div>
                                ))}
                            </Space>
                        </div>

                        {/* Social Platforms */}
                        {!isEmpty(socials) && (
                            <div
                                className={`personal-info-social ${
                                    socials.length < 4
                                        ? 'justify-start'
                                        : 'justify-between'
                                }`}
                            >
                                {socials.map((item, index) => {
                                    return (
                                        <img
                                            key={index}
                                            src={`/images/${item.platform}.svg`}
                                            alt={item.platform}
                                            onClick={() =>
                                                window.open(
                                                    dynamicExternalLink(
                                                        item.link
                                                    ),
                                                    '_blank'
                                                )
                                            }
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                        />
                                    )
                                })}
                            </div>
                        )}
                    </Card>
                </div>

                {/* Fixed bottom buttons */}
                {!showSideMenu && (
                    <div className="personal-info-fixed-bottom">
                        <div style={{ display: 'flex', gap: '40px' }}>
                            <Button
                                className="personal-info-prequalify-btn"
                                style={{ flex: 1 }}
                                onClick={() => {
                                    preQualify()
                                }}
                            >
                                Pre-qualified today
                            </Button>
                            <Button
                                type="primary"
                                className="personal-info-contact-btn"
                                style={{ flex: 1 }}
                                onClick={() => {
                                    contact()
                                }}
                            >
                                Contact
                            </Button>
                        </div>
                    </div>
                )}
            </>
        )
    }

    return (
        <div className="personal-info-container">
            <Card className="personal-info-card">
                {!isEmpty(basicInfo.file) && basicInfo.file?.[0]?.url ? (
                    <img
                        src={basicInfo.file[0].url}
                        alt={basicInfo.name}
                        className="personal-info-avatar"
                    />
                ) : (
                    <div
                        className="personal-info-avatar"
                        style={{
                            backgroundColor: '#d9d9d9',
                        }}
                        aria-label={basicInfo.name}
                    />
                )}
                <div className="personal-info-name">{name}</div>

                <div className="personal-info-details">
                    {/* NMLS Number and Verification Status */}
                    <div className="personal-info-label">
                        NMLS
                        <span className="personal-info-value">{` # ${basicInfo.nmls}`}</span>
                    </div>

                    {/* Company Information */}
                    <div className="personal-info-label">
                        Works at
                        <span className="personal-info-value">{` ${aboutInfo.companyName}`}</span>
                    </div>

                    {/* Language */}
                    <div className="personal-info-label">
                        Language
                        <span className="personal-info-value">{` ${languages}`}</span>
                    </div>
                </div>

                {/* Contact Information */}
                <div className="personal-info-contact">
                    <Space
                        direction="vertical"
                        size={12}
                        className="personal-info-contact-space"
                    >
                        {validContactInfos.map((item, index) => (
                            <div
                                key={index}
                                className="personal-info-contact-item"
                            >
                                <img
                                    src={`/images/${item.icon}`}
                                    alt={item.alt}
                                />
                                <span>{item.text}</span>
                            </div>
                        ))}
                    </Space>
                </div>

                {/* Contact Buttons */}
                <Space
                    direction="vertical"
                    size={12}
                    className="personal-info-buttons"
                >
                    <Button
                        type="primary"
                        className="personal-info-contact-btn"
                        style={{ width: '100%' }}
                        onClick={() => {
                            contact()
                        }}
                    >
                        Contact
                    </Button>
                    <Button
                        className="personal-info-prequalify-btn"
                        style={{ width: '100%' }}
                        onClick={() => {
                            preQualify()
                        }}
                    >
                        Pre-qualify today
                    </Button>
                </Space>

                {/* Social Platforms */}
                {!isEmpty(socials) && (
                    <div
                        className={`personal-info-social ${
                            socials.length < 4
                                ? 'justify-start'
                                : 'justify-between'
                        }`}
                    >
                        {socials.map((item, index) => {
                            return (
                                <img
                                    key={index}
                                    src={`/images/${item.platform}.svg`}
                                    alt={item.platform}
                                    onClick={() =>
                                        window.open(
                                            dynamicExternalLink(item.link),
                                            '_blank'
                                        )
                                    }
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                />
                            )
                        })}
                    </div>
                )}
            </Card>
        </div>
    )
}

export default PersonalInfoCard
